import React from 'react'
import {Box} from '../../../webui/Box'
import {Grid} from '../../../webui/Grid'
import {TextField, TextFieldProps} from '../../../webui/inputs/TextField'
import {Typography} from '../../../webui/Typography'
import {EditCity} from '../edit/EditCity'
import {EditableMap} from '../map/EditableMap'
import {
  LOCATION_TRANSLATION_LANGUAGE_LABELS,
  NON_CANONICAL_LOCATION_TRANSLATION_LANGUAGES,
  NonCanonicalLocationTranslationLanguage,
} from '../translationLanguages'

export interface LocationTranslationFormEntry {
  description: string
}

// Only the non-canonical languages live in the form's Translations section.
// English is taken from the top-level Description field on save.
export type LocationTranslationsForm = Record<NonCanonicalLocationTranslationLanguage, LocationTranslationFormEntry>

export interface LocationForm {
  name: string
  description: string
  locality?: string
  lat: string
  long: string
  translations?: LocationTranslationsForm
}

interface AddLocationProps {
  form: LocationForm
  updateField: <T extends keyof LocationForm>(field: T, value: LocationForm[T]) => void
}

// Slottsskogen
const DEFAULT_POSITION: [number, number] = [57.7089, 11.9746]

export const AddLocation: React.FC<AddLocationProps> = ({form, updateField}) => {
  const onNameChange: TextFieldProps['onChange'] = (value) => {
    updateField('name', value)
  }

  const onDescriptionChange: TextFieldProps['onChange'] = (value) => {
    updateField('description', value)
  }

  const onLocalityChange = (value: string) => {
    updateField('locality', value)
  }

  const onLatChange: TextFieldProps['onChange'] = (value) => {
    updateField('lat', value)
  }

  const onLongChange: TextFieldProps['onChange'] = (value) => {
    updateField('long', value)
  }

  const onTranslationDescriptionChange =
    (lang: NonCanonicalLocationTranslationLanguage): TextFieldProps['onChange'] =>
    (value) => {
      if (!form.translations) return
      updateField('translations', {
        ...form.translations,
        [lang]: {
          ...form.translations[lang],
          description: value,
        },
      })
    }

  const initialMarkerPosition = (() => {
    const latRaw = (form.lat ?? '').trim()
    const longRaw = (form.long ?? '').trim()

    // IMPORTANT: Number('') === 0, so we must guard empties explicitly
    if (!latRaw || !longRaw) {
      return [DEFAULT_POSITION[0], DEFAULT_POSITION[1]] as [number, number]
    }

    const lat = Number(latRaw)
    const long = Number(longRaw)

    if (Number.isFinite(lat) && Number.isFinite(long)) {
      return [lat, long] as [number, number]
    }

    return [DEFAULT_POSITION[0], DEFAULT_POSITION[1]] as [number, number]
  })()

  const onPositionChange = (lat: number, long: number) => {
    updateField('lat', String(lat))
    updateField('long', String(long))
  }

  return (
    <Grid>
      <Typography variant={'h5'}>Location</Typography>

      <Grid container gap={1}>
        {/* Row 1 */}
        <Grid container gap={1}>
          <Grid>
            <TextField label={'Name'} onChange={onNameChange} value={form.name} />
          </Grid>

          <Grid>
            <TextField label={'English Description'} onChange={onDescriptionChange} value={form.description} />
          </Grid>

          <Grid>
            <EditCity onChange={onLocalityChange} value={form.locality} />
          </Grid>
        </Grid>

        {/* Row 2: Latitude + Longitude */}
        <Grid container gap={1}>
          <Grid>
            <TextField label={'Latitude'} onChange={onLatChange} value={form.lat} />
          </Grid>

          <Grid>
            <TextField label={'Longitude'} onChange={onLongChange} value={form.long} />
          </Grid>
        </Grid>
      </Grid>

      {(() => {
        const translations = form.translations
        if (!translations) return null
        return (
          <Box mt={3}>
            <Typography variant="subtitle2" fontWeight={600}>
              Description translations
            </Typography>
            <Typography variant="body2" sx={{mb: 1, opacity: 0.8}}>
              Translations for additional languages. Optional — missing languages fall back to English (the Description above).
            </Typography>
            {NON_CANONICAL_LOCATION_TRANSLATION_LANGUAGES.map((lang) => (
              <Box key={lang} mt={1.5} p={1.5} border="1px solid rgba(0,0,0,0.08)" borderRadius={4}>
                <Typography variant="subtitle2" fontWeight={600}>
                  {LOCATION_TRANSLATION_LANGUAGE_LABELS[lang]}
                </Typography>
                <Box mt={0.5}>
                  <Typography variant="body2">Description</Typography>
                  <TextField
                    id={`add-location-translation-${lang}-description`}
                    value={translations[lang].description}
                    onChange={onTranslationDescriptionChange(lang)}
                  />
                </Box>
              </Box>
            ))}
          </Box>
        )
      })()}

      <Grid mt={2}>
        <EditableMap initialMarkerPosition={initialMarkerPosition} onPositionChange={onPositionChange} />
      </Grid>
    </Grid>
  )
}
